import { __assign } from "tslib";
import { makeRequestParams } from '../../utils/request-utils';
/**
 * Generates asset/content request payloads. Doesn't do the request itself
 * @param {string} [userToken] - Undefined in case of unauthenticated requests (shortform)
 * @param {Object} config - Playback Service config
 * @param {Object} requestOptions - Extension of OvpPlayoutRequestOptions
 * @param {Object} contentConfig
 * @param {Object} contentConfig.contentType - Content Type for the request
 * @param {Object} contentConfig.method - Method for the request
 * @param {Object} contentConfig.endpointKey - Where in the config it's the endpoint located
 * @returns {Array} Content Options tuple. URL as first element, payload as second element
 */
function buildRequestParams(userToken, config, requestOptions, contentType) {
    var _a = config.thirdParties, thirdParties = _a === void 0 ? [] : _a, timeouts = config.timeouts, deviceModel = config.deviceModel, configHdcp = config.hdcpEnabled;
    var requestHdcp = requestOptions.hdcpEnabled, legacyPR = requestOptions.requiresPlayReadyPersistentLicense, supportedColourSpaces = requestOptions.supportedColourSpaces, maxVideoFormat = requestOptions.maxVideoFormat, contentId = requestOptions.contentId, providerVariantId = requestOptions.providerVariantId, coppa = requestOptions.coppaApplies, isPrefetch = requestOptions.isPrefetch, personaParentalControlRating = requestOptions.personaParentalControlRating, journeyContext = requestOptions.journeyContext, parentalControlPin = requestOptions.parentalControlPin, _b = requestOptions.capabilities, capabilities = _b === void 0 ? [] : _b, pass = requestOptions.passActivationObject, serviceKey = requestOptions.serviceKey, _c = requestOptions.timeShiftEnabled, timeShiftEnabled = _c === void 0 ? serviceKey ? false : undefined : _c, variantCapable = requestOptions.variantCapable, robustness = requestOptions.robustness;
    var headers = __assign(__assign({}, config.headers), { userToken: userToken, coppa: coppa, isPrefetch: isPrefetch, journeyContext: journeyContext, 
        // Omit the header in case of unauthenticated request. Should this be here?
        ovpPinOverride: userToken ? "".concat(parentalControlPin === undefined) : undefined });
    var platform = config.headers.platform;
    var payload = {
        serviceKey: serviceKey,
        device: {
            capabilities: capabilities,
            maxVideoFormat: maxVideoFormat,
            supportedColourSpaces: supportedColourSpaces,
            legacyPR: legacyPR,
            model: deviceModel || platform,
            hdcpEnabled: requestHdcp !== undefined ? requestHdcp : configHdcp,
        },
        client: {
            thirdParties: thirdParties,
            timeShiftEnabled: timeShiftEnabled,
        },
        contentId: contentId,
        providerVariantId: providerVariantId,
        parentalControlPin: parentalControlPin,
        personaParentalControlRating: personaParentalControlRating,
        pass: pass,
    };
    if (variantCapable) {
        payload.client.variantCapable = variantCapable;
    }
    if (robustness) {
        payload.device.robustness = robustness;
    }
    var requestParams = makeRequestParams('POST', contentType, headers, timeouts, payload);
    requestParams.statusCodeCheck = false;
    return requestParams;
}
/**
 * Generates shortform request payloads. Doesn't do the request itself
 * @param {Object} config - Playback Service config
 * @param {Object} requestOptions - Extension of OvpPlayoutRequestOptions
 */
export function getShortformContentPayload(config, requestOptions) {
    var requestParams = buildRequestParams(undefined, config, requestOptions, 'application/vnd.trailer-vod.v1+json');
    return [config.endpoints.playback.getShortformAsset, requestParams];
}
/**
 * Generates linear request payloads. Doesn't do the request itself
 * @param {Object} config - Playback Service config
 * @param {Object} requestOptions - Extension of OvpPlayoutRequestOptions
 */
export function getLinearContentPayload(userToken, config, requestOptions) {
    var requestParams = buildRequestParams(userToken, config, requestOptions, 'application/vnd.playlive.v1+json');
    return [config.endpoints.playback.getLiveContent, requestParams];
}
/**
 * Generates VOD request payloads. Doesn't do the request itself
 * @param {Object} config - Playback Service config
 * @param {Object} requestOptions - Extension of OvpPlayoutRequestOptions
 */
export function getVodContentPayload(userToken, config, requestOptions) {
    var requestParams = buildRequestParams(userToken, config, requestOptions, 'application/vnd.playvod.v1+json');
    return [config.endpoints.playback.getVODAsset, requestParams];
}
/**
 * Generates Event request payloads. Doesn't do the request itself
 * @param {Object} config - Playback Service config
 * @param {Object} requestOptions - Extension of OvpPlayoutRequestOptions
 */
export function getEventContentPayload(userToken, config, requestOptions) {
    var requestParams = buildRequestParams(userToken, config, requestOptions, 'application/vnd.playevent.v1+json');
    return [config.endpoints.playback.getEventAsset, requestParams];
}
//# sourceMappingURL=content-request.js.map